// @ts-nocheck
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';

import CircularProgress from '@mui/material/CircularProgress';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import {
    TableContainer,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableSortLabel,
    TableBody,
    TextField,
    Button,
    Select,
    TablePagination,
    MenuItem,
    InputLabel,
    FormControl,
    Chip,
    InputAdornment,
} from '@mui/material';

import { capitalizeFirstLetter, renderKr } from 'utils/formatter';
import { teal } from 'api';
import { getAccessOrders } from 'store/slices/orders';
import paths from 'paths';

import { selectAccessOrders } from 'store';
import { statusOptions } from 'utils/constants';
import CreateOrderButton from './CreateOrderButton';
import { useOrdersQueryState } from './hooks/useOrdersQueryState';
import { useAccessProductOptions } from './hooks/useAccessProductOptions';
import ProductOptionsSelect from './ProductOptionsSelect';
import {
    calculateOrderTotal,
    getAdvisorDisplayName,
    getCustomerDisplayName,
    getProductsLabel,
    isSubscriptionOrder,
} from './services/ordersTable';

const debounceSearch = (func, timeout = 300) => {
    let timer;
    return (...args) => {
        clearTimeout(timer);
        timer = setTimeout(() => {
            func.apply(this, args);
        }, timeout);
    };
};

const renderDateTimeLongWithYear = (date) => new Date(date).toLocaleString('da-DK', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
});

const dateColumnSx = {
    minWidth: 240,
    whiteSpace: 'nowrap',
};

const Orders = () => {
    const dispatch = useDispatch();
    const { organisationId } = useParams();
    const user = useSelector((state) => state.auth.user);
    const organisation = useSelector((state) => state.auth.user.organisations.find((org) => org.organisationId === organisationId));
    const loadedOrders = useSelector((state) => state.accessOrders.loaded);
    const loading = useSelector((state) => state.accessOrders.loading);
    const count = useSelector((state) => state.accessOrders.count);
    const orders = useSelector(selectAccessOrders);
    const [organisationUsers, setOrganisationUsers] = useState([]);

    const {
        setSearch,
        page,
        setPage,
        pageSize,
        setPageSize,
        sortBy,
        setSortBy,
        sortOrder,
        setSortOrder,
        filters,
        searchRef,
        buildParams,
        syncUrl,
        updateFilter,
        clearFilters,
    } = useOrdersQueryState();

    const navigate = useNavigate();

    const showLegal = organisation.organisationId === 'jura-nord-advokater-aps' || organisation.organisationId === 'alliance-advokater';
    const { lawProductOptions, nonLawProductOptions } = useAccessProductOptions(organisationId, showLegal);
    const selectedAdvisorId = filters.find((x) => x.property === 'assignedTo.userId')?.value ?? '';
    const advisorOptions = organisationUsers
        .map((advisor) => ({ ...advisor, resolvedUserId: advisor.userId || advisor.id }))
        .filter((advisor) => advisor?.resolvedUserId && advisor.resolvedUserId !== user.userId)
        .map((advisor) => ({
            userId: advisor.resolvedUserId,
            name: advisor.name || `${advisor.firstName || ''} ${advisor.lastName || ''}`.trim() || advisor.email || advisor.resolvedUserId,
        }));

    const fetchOrders = useCallback(() => {
        const params = buildParams();
        dispatch(getAccessOrders(organisationId, params));
        syncUrl(params);
    }, [buildParams, dispatch, organisationId, syncUrl]);

    useEffect(() => {
        fetchOrders();
    }, [fetchOrders]);

    useEffect(() => {
        let active = true;

        const fetchOrganisationUsers = async () => {
            const usersInOrganisation = await teal.getUsersInOrganisation(organisationId);
            if (active) {
                setOrganisationUsers(usersInOrganisation || []);
            }
        };

        fetchOrganisationUsers();
        return () => {
            active = false;
        };
    }, [organisationId]);

    const handleChangeSorting = (newSortBy) => {
        setSortBy(newSortBy);
        const flippedSortOrder = sortOrder === 'asc' ? 'desc' : 'asc';
        let newSortOrder = 'asc';
        if (sortBy === newSortBy) {
            newSortOrder = flippedSortOrder;
        } else if (newSortBy === 'orderNumber') {
            newSortOrder = 'desc';
        }
        setSortOrder(newSortOrder);
    };

    const handleSearchChange = useMemo(
        () => debounceSearch((e) => setSearch(e.target.value)),
        [setSearch],
    );

    const getOrderPath = (orderId) => paths.penlyAccessOrder.getPath(organisationId, orderId);

    return (
        <>
            { !loadedOrders && (
                <div className="App-section-loading">
                    <CircularProgress color="primary" size={ 40 } />
                </div>
            ) }
            { loadedOrders && (
                <>
                    <div style={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center', gap: 16, minWidth: 1300 }}>
                        <TextField
                            size="small"
                            placeholder="Søg ordrer..."
                            onChange={ handleSearchChange }
                            inputRef={ searchRef }
                            autoComplete="off"
                            sx={{
                                width: 360,
                                backgroundColor: 'white',
                                borderRadius: 1,
                                paddingLeft: 1,
                                paddingRight: 1,
                                '& .MuiOutlinedInput-root': {
                                    '& fieldset': {
                                        borderColor: 'rgba(0, 0, 0, 0.23)',
                                    },
                                    '&:hover fieldset': {
                                        borderColor: 'rgba(0, 0, 0, 0.87)',
                                    },
                                    '&.Mui-focused fieldset': {
                                        borderColor: 'primary.main',
                                    },
                                },
                            }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon />
                                    </InputAdornment>
                                ),
                            }}
                        />
                        <div style={{ display: 'flex', gap: 8 }}>
                            <CreateOrderButton disabled={ loading } isLaw={ showLegal } customer={ undefined } />
                            <Button disabled={ loading } endIcon={ <RefreshIcon /> } onClick={ fetchOrders }>
                                { loading ? 'Opdaterer' : 'Opdatér' }
                            </Button>
                        </div>
                    </div>

                    <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
                        <ProductOptionsSelect
                            labelId="product-label"
                            label="Produkt"
                            value={ filters.find((x) => x.property === 'items.product.id')?.value ?? '' }
                            onChange={ (e) => updateFilter('items.product.id', 'equals', e.target.value) }
                            lawProductOptions={ lawProductOptions }
                            nonLawProductOptions={ nonLawProductOptions }
                            variant="standard"
                            sx={{ m: 1, minWidth: 120 }}
                        />
                        <FormControl variant="standard" sx={{ m: 1, minWidth: 120 }}>
                            <InputLabel id="status-label">Status</InputLabel>
                            <Select
                                labelId="status-label"
                                value={ filters.find((x) => x.property === 'status')?.value ?? '' }
                                onChange={ (e) => updateFilter('status', 'equals', e.target.value) }
                                label="Status"
                            >
                                { statusOptions.map((status) => (
                                    <MenuItem key={ status.value } value={ status.value }>
                                        { status.name }
                                    </MenuItem>
                                )) }
                            </Select>
                        </FormControl>
                        <FormControl variant="standard" sx={{ m: 1, minWidth: 140 }}>
                            <InputLabel id="orders-scope-label">Rådgiver</InputLabel>
                            <Select
                                labelId="orders-scope-label"
                                value={ selectedAdvisorId === user.userId ? 'mine' : selectedAdvisorId }
                                onChange={ (e) => updateFilter('assignedTo.userId', 'equals', e.target.value === 'mine' ? user.userId : e.target.value) }
                                label="Ordrer"
                            >
                                <MenuItem value="mine">Mig</MenuItem>
                                { advisorOptions.map((advisor) => (
                                    <MenuItem key={ advisor.userId } value={ advisor.userId }>{ advisor.name }</MenuItem>
                                )) }
                            </Select>
                        </FormControl>
                        <Button style={{ marginTop: 20 }} onClick={ clearFilters }>Ryd filtre</Button>
                    </div>
                    <TableContainer sx={{ mt: 0 }}>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell align="left">
                                        <TableSortLabel active={ sortBy === 'orderNumber' } direction={ sortBy === 'orderNumber' ? sortOrder : 'asc' } onClick={ () => handleChangeSorting('orderNumber') }>
                                            Ordre #
                                        </TableSortLabel>
                                    </TableCell>
                                    <TableCell align="left">
                                        <TableSortLabel active={ sortBy === 'customer.email' } direction={ sortBy === 'customer.email' ? sortOrder : 'asc' } onClick={ () => handleChangeSorting('customer.email') }>
                                            Kunde
                                        </TableSortLabel>
                                    </TableCell>
                                    <TableCell align="left" sx={ dateColumnSx }>
                                        <TableSortLabel active={ sortBy === 'createdAt' } direction={ sortBy === 'createdAt' ? sortOrder : 'asc' } onClick={ () => handleChangeSorting('createdAt') }>
                                            Bestillingsdato
                                        </TableSortLabel>
                                    </TableCell>
                                    <TableCell align="left" sx={ dateColumnSx }>
                                        <TableSortLabel active={ sortBy === 'bookings.startTime' } direction={ sortBy === 'bookings.startTime' ? sortOrder : 'asc' } onClick={ () => handleChangeSorting('bookings.startTime') }>
                                            Mødedato
                                        </TableSortLabel>
                                    </TableCell>
                                    <TableCell align="left">
                                        <TableSortLabel active={ sortBy === 'assignedAdvisor.name' } direction={ sortBy === 'assignedAdvisor.name' ? sortOrder : 'asc' } onClick={ () => handleChangeSorting('assignedAdvisor.name') }>
                                            Rådgiver
                                        </TableSortLabel>
                                    </TableCell>
                                    <TableCell align="left">
                                        <TableSortLabel active={ sortBy === 'sharingIds' } direction={ sortBy === 'sharingIds' ? sortOrder : 'asc' } onClick={ () => handleChangeSorting('sharingIds') }>
                                            Deling
                                        </TableSortLabel>
                                    </TableCell>
                                    <TableCell align="left">Mødeklar</TableCell>
                                    <TableCell align="left">
                                        <TableSortLabel active={ sortBy === 'items.product.name' } direction={ sortBy === 'items.product.name' ? sortOrder : 'asc' } onClick={ () => handleChangeSorting('items.product.name') }>
                                            Produkter
                                        </TableSortLabel>
                                    </TableCell>
                                    <TableCell align="left">Pris</TableCell>
                                    <TableCell align="left">
                                        <TableSortLabel active={ sortBy === 'status' } direction={ sortBy === 'status' ? sortOrder : 'asc' } onClick={ () => handleChangeSorting('status') }>
                                            Status
                                        </TableSortLabel>
                                    </TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                { orders.map((order) => {
                                    const totalPrice = calculateOrderTotal(order.items);
                                    const hasSubscription = isSubscriptionOrder(order.items);
                                    const customerDisplayName = getCustomerDisplayName(order);
                                    const showCustomerEmail = Boolean(order.customer?.email && order.customer?.userId);

                                    return (
                                        <TableRow key={ order.orderId } onClick={ () => navigate(getOrderPath(order.orderId)) } style={{ cursor: 'pointer' }}>
                                            <TableCell align="left" sx={{ fontWeight: 700 }}>{ order.orderNumber }</TableCell>
                                            <TableCell align="left">
                                                <div>{ customerDisplayName }</div>
                                                { showCustomerEmail && <div style={{ fontSize: '0.8em', color: '#666' }}>{ order.customer.email }</div> }
                                            </TableCell>
                                            <TableCell align="left" sx={ dateColumnSx }>{ capitalizeFirstLetter(renderDateTimeLongWithYear(order.createdAt)) }</TableCell>
                                            <TableCell align="left" sx={ dateColumnSx }>
                                                { order.bookings && order.bookings[0] ? capitalizeFirstLetter(renderDateTimeLongWithYear(order.bookings[0].startTime)) : '' }
                                            </TableCell>
                                            <TableCell align="left">{ getAdvisorDisplayName(order) }</TableCell>
                                            <TableCell align="left">
                                                <Chip label={ order.sharingIds && order.sharingIds[0] ? 'Ja' : 'Nej' } />
                                            </TableCell>
                                            <TableCell align="left">
                                                { order.dataCollectionProgress?.currentStep && (
                                                    order.dataCollectionProgress.completed
                                                        ? <Chip color="primary" label="Ja" />
                                                        : <Chip label="Nej" />
                                                ) }
                                            </TableCell>
                                            <TableCell align="left">{ getProductsLabel(order.items) }</TableCell>
                                            <TableCell align="left">
                                                { hasSubscription ? `${renderKr(totalPrice)} / år` : renderKr(totalPrice) }
                                            </TableCell>
                                            <TableCell align="left">
                                                <Chip label={ statusOptions.find((x) => x.value === order.status)?.name || order.status } />
                                            </TableCell>
                                        </TableRow>
                                    );
                                }) }
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <TablePagination
                        component="div"
                        count={ count }
                        page={ page || 0 }
                        onPageChange={ (_, newPage) => setPage(newPage) }
                        rowsPerPage={ pageSize || 50 }
                        onRowsPerPageChange={ (event) => {
                            setPageSize(parseInt(event.target.value, 10));
                            setPage(0);
                        } }
                        labelRowsPerPage="Rækker pr. side"
                        labelDisplayedRows={ ({ from, to, count: totalCount }) => `${from}-${to} af ${totalCount}` }
                    />
                </>
            ) }
        </>
    );
};

export default Orders;
